import { Button, Tooltip } from '@fluentui/react-components';
import { ArrowUndoRegular, CheckmarkRegular } from '@fluentui/react-icons';
import { useContext } from 'react';

import { useUpdateResolution } from '../../api';
import { TComment } from '../../types';

import { AccessType, TPermissions } from '@modules/access';
import { TDocument } from '@modules/documents';
import { TFile } from '@modules/files';
import { USER_GROUPS, SignInContext } from '@modules/users';
import { TInlineBlock } from '@modules/inlineBlocks';

type TProps = {
  userId: string;
  item: TDocument | TFile | TInlineBlock;
  comment: TComment;
  permissions: TPermissions;
};

const CardResolveButton = ({ userId, item, comment, permissions }: TProps) => {
  const { user } = useContext(SignInContext);

  // --------- API logic ---------
  const updateResolution = useUpdateResolution();

  const handleResolved = (newResolved: boolean) => {
    updateResolution.mutate({
      itemType: item.type,
      itemId: item._id,
      commentId: comment._id,
      resolved: newResolved,
    });
  };

  // --------- show the "unresolved" button or not ---------
  const draftIsOpen = (item as TDocument).draft;
  const latestDocumentVersion = item.versions[0];

  // Options #1 – draft is open for editing
  const isEditingDraft = draftIsOpen && comment.versionResolved === 'draft';
  // Options #2 – comment is the latest version of the item (document)
  const isLatestVersion = !draftIsOpen && comment.versionResolved === latestDocumentVersion.id;

  const showUnresolvedButton = isEditingDraft || isLatestVersion;

  // --------- show buttons at all or not ---------
  // Option #1
  const isOwner = user._id === userId;
  // Option #2
  const hasEditPermission = permissions.accessRight >= AccessType.EDIT;
  const isAuthorGroup = user.groups.includes(USER_GROUPS.AUTHOR);

  const showButtons = (hasEditPermission && isAuthorGroup) || isOwner;

  if (!showButtons) {
    return null;
  }

  return (
    <>
      {/* no item version of comment resolution means the comment is not resolved */}
      {!comment.versionResolved && (
        <Tooltip content="Resolve comment" relationship="label" withArrow>
          <Button
            icon={<CheckmarkRegular />}
            appearance="transparent"
            size="small"
            onClick={(e) => {
              // Avoid unselecting the comment card
              e.stopPropagation();
              handleResolved(true);
            }}
          />
        </Tooltip>
      )}

      {showUnresolvedButton && (
        <Tooltip content="Reopen comment" relationship="label" withArrow>
          <Button
            icon={<ArrowUndoRegular />}
            appearance="transparent"
            size="small"
            onClick={(e) => {
              // Avoid unselecting the comment card
              e.stopPropagation();
              handleResolved(false);
            }}
          />
        </Tooltip>
      )}
    </>
  );
};

export default CardResolveButton;
